import { formatInTimeZone } from 'date-fns-tz';

import {
    cumulativeUpToSameHour,
    sameTimeDeltaPct,
} from '@apps/web/components/admin/outreach_observability/comparison_utils';
import {
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachWindowSelection,
    outreachWindowSummary,
} from '@apps/web/components/admin/outreach_observability/constants';
import { type KpiRunMetric } from '@apps/web/components/admin/outreach_observability/runs_drill_dialog';
import { cn } from '@apps/web/lib/utils';
import {
    type ComparisonDay,
    type ComparisonsSection,
    type HourlyKpiBucket,
    type OutreachObservabilityPulse,
} from '@libraries/typescript/api_client';

// The run-grain fields both the live hourly series (HourlyKpiBucket) and the
// comparison hourly series (ComparisonHourBucket) structurally share, so one
// accessor drives the today and the reference cumulatives.
type FunnelBucket = {
    readonly bucket_start: string;
    readonly num_runs: number;
    readonly contacted_run_count: number;
    readonly responded_run_count: number;
};

// `field` is null for the got-yes and filled stages: those are run counts on
// the pulse, but the comparison series only tracks shift-grain yes/filled
// counts — comparing a run count to a shift count is a grain mismatch, so those
// two stages show no vs-prior-day delta at all.
type FunnelStageDef = {
    readonly key: string;
    readonly label: string;
    readonly count: number;
    readonly field: ((bucket: FunnelBucket) => number) | null;
    // KPI-runs metric this stage drills into.
    readonly metric: KpiRunMetric;
};

type FunnelDelta = {
    readonly label: string;
    readonly pct: number | null;
};

function DeltaChip({ delta }: { readonly delta: FunnelDelta }) {
    if (delta.pct === null) {
        return (
            <span className="text-muted-foreground inline-flex items-center gap-0.5 tabular-nums">
                <span className="uppercase">{delta.label}</span>
                <span>—</span>
            </span>
        );
    }
    // More runs reaching a funnel stage is always the good direction, so up is
    // green and down is red regardless of which stage it is.
    const isUp = delta.pct >= 0;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 tabular-nums',
                isUp
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400',
            )}
        >
            <span className="uppercase">{delta.label}</span>
            <span>
                {isUp ? '▲' : '▼'}
                {Math.abs(delta.pct).toFixed(0)}%
            </span>
        </span>
    );
}

function FunnelStage({
    stage,
    previousCount,
    totalCount,
    deltas,
    onSelect,
}: {
    readonly stage: FunnelStageDef;
    readonly previousCount: number | null;
    readonly totalCount: number;
    readonly deltas: Array<FunnelDelta> | null;
    readonly onSelect?: () => void;
}) {
    const widthPct = totalCount > 0 ? (stage.count / totalCount) * 100 : 0;
    const pctOfPrevious =
        previousCount !== null && previousCount > 0
            ? (stage.count / previousCount) * 100
            : null;
    const pctOfTotal = totalCount > 0 ? (stage.count / totalCount) * 100 : 0;
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="font-medium">{stage.label}</span>
                <span className="text-muted-foreground text-xs tabular-nums">
                    {pctOfPrevious !== null
                        ? `${pctOfPrevious.toFixed(0)}% of prev · `
                        : ''}
                    {pctOfTotal.toFixed(0)}% of total
                </span>
            </div>
            <button
                type="button"
                onClick={onSelect}
                disabled={onSelect === undefined}
                className={cn(
                    'flex items-center gap-3 rounded-md text-left',
                    onSelect !== undefined &&
                        'hover:bg-muted/40 -mx-1 cursor-pointer px-1',
                )}
            >
                <div className="bg-muted h-6 flex-1 overflow-hidden rounded-md">
                    <div
                        className="h-full rounded-md"
                        style={{
                            backgroundColor: 'var(--chart-1)',
                            width: `${widthPct}%`,
                        }}
                    />
                </div>
                <span className="w-14 text-right text-lg font-semibold tabular-nums">
                    {stage.count}
                </span>
            </button>
            {deltas !== null ? (
                <div className="flex flex-wrap items-center gap-x-3 text-xs">
                    {deltas.map((delta) => (
                        <DeltaChip key={delta.label} delta={delta} />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

export function OutreachFunnel({
    pulse,
    comparisons,
    kpiHourlyTrends,
    window: liveWindow,
    onStageClick,
}: {
    readonly pulse: OutreachObservabilityPulse;
    readonly comparisons: ComparisonsSection | null;
    readonly kpiHourlyTrends: Array<HourlyKpiBucket>;
    readonly window: OutreachWindowSelection;
    readonly onStageClick?: (stage: {
        readonly metric: KpiRunMetric;
        readonly label: string;
    }) => void;
}) {
    const stages: Array<FunnelStageDef> = [
        {
            key: 'created',
            label: 'Created',
            count: pulse.num_runs,
            field: (bucket) => bucket.num_runs,
            metric: 'runs',
        },
        {
            key: 'contacted',
            label: 'Contacted',
            count: pulse.contacted_run_count,
            field: (bucket) => bucket.contacted_run_count,
            metric: 'contacted',
        },
        {
            key: 'responded',
            label: 'Responded',
            count: pulse.responded_run_count,
            field: (bucket) => bucket.responded_run_count,
            metric: 'responded',
        },
        // No `field`: got_yes_count / filled_count are run counts, but the
        // comparison series only exposes shift-grain equivalents, so these two
        // stages intentionally carry no vs-prior-day delta.
        {
            key: 'got_yes',
            label: 'Got yes',
            count: pulse.got_yes_count,
            field: null,
            metric: 'got_yes',
        },
        {
            key: 'filled',
            label: 'Filled',
            count: pulse.filled_count,
            field: null,
            metric: 'filled',
        },
    ];

    // Align "today" and the comparison days on the same ET hour-of-day: sum each
    // series up to the current hour so a partial today is compared like-for-like
    // against a full prior day. Today's cumulative comes from the live hourly
    // buckets narrowed to today's ET date (the trailing-48h series spans two
    // dates, which would otherwise double-count each hour-of-day).
    const nowEtHour = Number(
        formatInTimeZone(
            new Date(pulse.window_end),
            OUTREACH_ANALYTICS_TIMEZONE,
            'H',
        ),
    );
    const todayEtDate = formatInTimeZone(
        new Date(pulse.window_end),
        OUTREACH_ANALYTICS_TIMEZONE,
        'yyyy-MM-dd',
    );
    const todayHourly = kpiHourlyTrends.filter(
        (bucket) =>
            formatInTimeZone(
                new Date(bucket.bucket_start),
                OUTREACH_ANALYTICS_TIMEZONE,
                'yyyy-MM-dd',
            ) === todayEtDate,
    );

    function stageDeltas(stage: FunnelStageDef): Array<FunnelDelta> | null {
        if (stage.field === null || comparisons === null) {
            return null;
        }
        const field = stage.field;
        const todayValue = cumulativeUpToSameHour(
            todayHourly,
            field,
            nowEtHour,
        );
        const referenceValue = (label: string, day: ComparisonDay) => ({
            label,
            pct: sameTimeDeltaPct(
                todayValue,
                cumulativeUpToSameHour(day.hourly, field, nowEtHour),
            ),
        });
        return [
            referenceValue('yest', comparisons.yesterday),
            referenceValue('7d', comparisons.week_ago),
        ];
    }

    if (pulse.num_runs === 0) {
        return (
            <div className="text-muted-foreground flex h-40 items-center justify-center rounded-md border text-sm">
                No runs in this window.
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
                {stages.map((stage, index) => (
                    <FunnelStage
                        key={stage.key}
                        stage={stage}
                        previousCount={
                            index === 0
                                ? null
                                : (stages[index - 1]?.count ?? null)
                        }
                        totalCount={pulse.num_runs}
                        deltas={stageDeltas(stage)}
                        onSelect={
                            onStageClick
                                ? () =>
                                      onStageClick({
                                          metric: stage.metric,
                                          label: stage.label,
                                      })
                                : undefined
                        }
                    />
                ))}
            </div>
            <p className="text-muted-foreground text-xs">
                Bars show the {outreachWindowSummary(liveWindow)} pipeline; the
                yest / 7d deltas compare today so far against the same ET hour
                on prior days. Got-yes and filled stages omit deltas because the
                comparison series tracks shifts, not runs.
            </p>
        </div>
    );
}
